<template lang="pug">
.media-detail(ref="detail", @click="$emit('close')")
  .media-container(ref="container")
    .toggle-info-button(@click.stop="showDetails = !showDetails") {{ showDetails ? "Details verbergen" : "Details anzeigen" }}
    div(style="position: absolute; left: 4vw; height: 4vw") {{ scale }}
    .media-viewer(
      ref="viewer",
      @mousedown="startDrag",
      @mousemove="onDrag",
      @mouseup="stopDrag",
      @mouseleave="stopDrag",
      @wheel.prevent="onWheel",
      @touchstart.passive="onTouchStart",
      @touchmove.prevent="onTouchMove",
      @touchend="onTouchEnd"
    )
      .media-transform(:style="transformStyle")
        img(
          v-if="mediaUrl && !isVideo",
          ref="media",
          :src="mediaUrl",
          draggable="false",
          @load="onImageLoad"
        )
        .face-box-wrapper(
          v-for="(face, index) in item.faces",
          :key="index",
          @click.stop="adminMode && selectFace(face)",
          :style="faceBoxStyle(face.box)",
          :class="{ 'admin-editable': adminMode, selected: selectedFace && selectedFace.index === face.index }"
        )
      .face-label(
        :style="faceLabelStyle(face)",
        v-for="face in item.faces",
        :key="'label-' + face.index"
      )
        span(v-if="!isEditing(face)", @click.stop="editLabel(face)") {{ face.name || "unbenannt" }}
        .edit-wrapper(v-else)
          input(
            v-model="editingName",
            :key="`input-${face.index}`",
            @focus="selectAllText($event)",
            @blur="submitLabel(face.index)",
            @keyup.enter="submitLabel(face.index)",
            @input="onInputChange",
            @keydown.down.prevent="highlightNext",
            @keydown.up.prevent="highlightPrev",
            @keydown.enter.prevent="selectHighlighted",
            :ref="'labelInput_' + face.index"
          )
          ul.suggestions(v-if="filteredGroups.length > 0")
            li(
              v-for="(s, i) in filteredGroups",
              :class="{ highlighted: i === highlightedIndex }",
              @mousedown.prevent="selectSuggestion(s)"
            ) {{ s.name }}
          .face-box

      video(
        ref="video",
        v-if="mediaUrl && isVideo",
        :src="mediaUrl",
        controls,
        autoplay,
        playsinline
      )

    .admin-tools(
      v-if="adminMode && selectedFace",
      @mousedown.stop,
      @mouseup.stop,
      @click.stop
    )
      button(@click="deleteSelectedFace") Entfernen
      select(v-model="selectedGroupId")
        option(value="") -- Gruppe wählen --
        option(v-for="g in groups", :key="g.id", :value="g.id") {{ g.name || g.id }}
      //- button(@click="markUnknown") Unbekannt

  media-info(v-if="item && showDetails", :item="item")
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import MediaInfo from "@/components/MediaInfo.vue";

export default {
  components: { MediaInfo },
  props: ["itemId", "adminMode"],
  data() {
    return {
      mediaUrl: null,
      scale: 1,
      translateX: 0,
      translateY: 0,
      dragging: false,
      dragStart: { x: 0, y: 0 },
      initialDistance: null,
      initialScale: 1,
      naturalSize: { width: 0, height: 0 },
      showDetails: false,
      naturalWidth: 0,
      naturalHeight: 0,
      isInFullscreen: false,

      selectedFace: null,
      selectedGroupId: null,
      editingFaceIndex: null,
      editingName: "",
      highlightedIndex: -1,
      filteredGroups: [],
    };
  },
  computed: {
    ...mapGetters({
      item: "media/item",
      groups: "faces/groups",
    }),
    groupMap() {
      const map = {};
      for (const g of this.groupList || []) {
        map[g.id] = g.name || g.id;
      }
      return map;
    },
    imageStyle() {
      return {
        transform: `translate(${this.translateX}px, ${this.translateY}px) scale(${this.scale}) `,
        transition: this.transitioning ? "transform 0.2s ease" : "none",
      };
    },
    dropdownStyle() {
      return {
        maxHeight: `${100 / this.scale}vh`,
        top: `${5 / this.scale}vw`,
      };
    },
    isVideo() {
      return this.item?.mime_type?.startsWith("video");
    },
    transformStyle() {
      const d = this.$refs.detail;
      const c = this.$refs.container;
      const m = this.$refs.media;
      const v = this.$refs.viewer;
      // console.log(d, c, m, v);
      return {
        transform: `translate(${this.translateX}px, ${this.translateY}px) scale(${this.scale})`,
        transformOrigin: "top left",
        transition: this.dragging ? "none" : "transform 0.1s ease-out",
        width: `${this.naturalWidth}px`,
        height: `${this.naturalHeight}px`,
        position: "relative",
      };
    },
  },
  async mounted() {
    document.addEventListener("fullscreenchange", this.onFullscreenChange);
    window.addEventListener("orientationchange", this.onOrientationChange);
    await this.getItem(this.itemId);
    this.mediaUrl = `https://gnatowski.ddns.net/api/media/content/${this.item.id}`;
    this.loadGroups();
  },
  beforeDestroy() {
    document.removeEventListener("fullscreenchange", this.onFullscreenChange);
    window.removeEventListener("orientationchange", this.onOrientationChange);
  },
  methods: {
    ...mapActions({
      getItem: "media/item",
      loadGroups: "faces/loadGroups",
      assignToGroupByName: "faces/assignToGroupByName",
    }),
    onFullscreenChange() {
      this.isInFullscreen = !!document.fullscreenElement;
    },
    async onOrientationChange() {
      const isLandscape = screen.orientation?.type?.startsWith("landscape");
      const videoEl = this.$refs.video;
      if (!videoEl) return;
      setTimeout(async () => {

        try {
          if (isLandscape) {
            // Immer versuchen, in Landscape in Vollbild zu gehen
            if (!document.fullscreenElement) {
              await videoEl.requestFullscreen?.();
            }
          } else 
          if (document.fullscreenElement) {
            // Immer versuchen, in Portrait Vollbild zu beenden, egal was fullscreenElement sagt
            await document.exitFullscreen?.();
          }
        } catch (e) {
          console.warn("Fehler beim (De)aktivieren des Vollbildmodus:", e);
        }
      }, 100); // Leichte Verzögerung, damit Rotation abgeschlossen ist
    },
    selectAllText(event) {
      const input = event.target;
      // Wartet einen Tick, um sicherzustellen, dass der Fokus abgeschlossen ist
      this.$nextTick(() => {
        input.select();
      });
    },
    scaledX(value) {
      return value * this.scale + this.translateX;
    },
    scaledY(value) {
      return value * this.scale + this.translateY;
    },
    filterGroups() {
      const q = this.editingName.toLowerCase();
      this.filteredGroups = this.groups.filter((g) =>
        g.name.toLowerCase().includes(q)
      );
      console.log("filter groups:", this.filteredGroups);
    },
    selectGroup(name) {
      this.edititgName = name;
      this.submitLabel(this.editingFaceIndex);
    },
    highlightNext() {
      if (this.highlightedIndex < this.filteredGroups.length - 1) {
        this.highlightedIndex++;
      }
    },
    highlightPrev() {
      if (this.highlightedIndex > 0) {
        this.highlightedIndex--;
      }
    },
    selectHighlighted() {
      if (this.highlightedIndex >= 0) {
        this.selectSuggestion(this.filteredGroups[this.highlightedIndex]);
      } else {
        this.submitLabel(this.editingFaceIndex);
      }
    },
    hideSuggestions() {
      setTimeout(() => (this.showSuggestions = false), 200); // Delay for click
    },
    selectSuggestion(group) {
      this.editingName = group.name;
      this.submitLabel(this.editingFaceIndex);
    },
    isEditing(face) {
      return this.editingFaceIndex === face.index;
    },
    editLabel(face) {
      console.log("edit Label", face);
      this.selectedFace = face;
      this.editingFaceIndex = face.index;
      this.editingName = face.name || "";
      this.filteredGroupNames = this.groups.map((g) => g.name);
      console.log("filtered groups", this.filteredGroupNames);
      this.$nextTick(() => {
        const ref = this.$refs["labelInput_" + face.index];
        const input = Array.isArray(ref) ? ref[0] : ref;
        console.log("input = ", input);
        setTimeout(() => {
          input?.focus?.();
        }, 0);
      });
    },
    onInputChange() {
      console.log("input change", this.editingName.toLowerCase());
      const input = this.editingName.toLowerCase();
      this.filteredGroups = this.groups.filter((g) =>
        g.name.toLowerCase().includes(input)
      );
      this.highlightedIndex = -1;
    },
    submitLabel(faceIndex) {
      if (!this.editingName.trim()) return;

      console.log("FACE:", faceIndex);

      this.$store
        .dispatch("faces/assignToGroupByName", {
          mediaId: this.item.id,
          faceIndex: faceIndex,
          groupName: this.editingName.trim(),
        })
        .then(() => {
          this.editingFaceIndex = null;
          this.editingName = "";
          this.getItem(this.itemId); // refresh
        });

      this.editingFaceIndex = null;
      this.editingName = "";
      this.filteredGroups = [];
      this.highlightedIndex = -1;
    },
    selectFace(face) {
      if (!this.selectedFace) this.selectedFace = face;
      else if (this.selectedFace.index == face.index) this.selectedFace = null;
      else this.selectedFace = face;
    },
    deleteSelectedFace() {
      if (!this.selectedFace) return;
      // API-Aufruf oder Vuex-Action:
      this.$store
        .dispatch("faces/deleteFace", this.selectedFace.id)
        .then(() => this.getItem(this.itemId)); // neu laden
    },
    assignToGroup() {
      if (this.selectedGroupId && this.selectedFace) {
        this.$store
          .dispatch("faces/assignToGroup", {
            faceId: this.selectedFace.id,
            groupId: this.selectedGroupId,
          })
          .then(() => {
            this.getItem(this.itemId);
            this.selectedFace = null;
            this.selectedGroupId = null;
          });
      }
    },
    onImageLoad() {
      const img = this.$refs.media;
      if (!img) return;

      // Warte, bis naturalWidth sicher verfügbar ist
      this.naturalWidth = img.naturalWidth;
      this.naturalHeight = img.naturalHeight;

      this.fitToScreen();
    },
    faceLabelStyle(face) {
      const x = this.scaledX(face.box.x + face.box.width / 2);
      const y = this.scaledY(face.box.y);
      const baseSize = 8; // 2vw bei scale = 1
      const fontSize = baseSize / this.scale; // bei scale=0.5 → 4, aber gedeckelt auf 3
      console.log('FONT SIZE: ', baseSize, this.scale.toFixed(2), fontSize.toFixed(2));
      const isSelected =
        this.selectedFace && this.selectedFace.index === face.index;
      return {
        position: "absolute",
        left: x + "px",
        top: y + "px",
        transform: "translate(-50%, -100%)",
        background: isSelected
          ? "rgba(0, 200, 0, 0.8)"
          : "rgba(255, 0, 0, 0.7)",
        padding: "2px 4px",
        fontSize: '12pt',
        zIndex: 10,
        pointerEvents: "auto",
        whiteSpace: "nowrap",
      };
    },
    faceLabelInputStyle(face) {
      const x = this.scaledX(face.box.x + face.box.width / 2);
      const y = this.scaledY(face.box.y);
      const baseSize = 4; // 2vw bei scale = 1
      const fontSize = baseSize / this.scale; // bei scale=0.5 → 4, aber gedeckelt auf 3
      return {
        position: "absolute",
        left: x + "px",
        top: y + "px",
        transform: "translate(-50%, -100%)",
        padding: "2px 4px",
        fontSize: `3vw`,
        zIndex: 10,
        pointerEvents: "auto",
        whiteSpace: "nowrap",
      };
    },
    faceBoxStyle(box) {
      const d = 4 / this.scale;
      return {
        position: "absolute",
        borderStyle: `solid`,
        borderWidth: `${d}px`,
        left: `${box.x}px`,
        top: `${box.y}px`,
        width: `${box.width}px`,
        height: `${box.height}px`,
      };
    },
    fitToScreen() {
      const img = this.$refs.media;
      const viewer = this.$refs.viewer;
      if (!img || !viewer) return;

      this.naturalWidth = img.naturalWidth;
      this.naturalHeight = img.naturalHeight;

      const vw = viewer.clientWidth;
      const vh = viewer.clientHeight;

      const scale = Math.min(vw / this.naturalWidth, vh / this.naturalHeight);

      const displayWidth = this.naturalWidth * scale;
      const displayHeight = this.naturalHeight * scale;

      this.scale = scale;
      this.translateX = (vw - displayWidth) / 2;
      this.translateY = (vh - displayHeight) / 2;

      // console.log("fitToScreen", {
      //   scale,
      //   displayWidth,
      //   displayHeight,
      //   vw,
      //   vh,
      //   translateX: this.translateX,
      //   translateY: this.translateY,
      // });
    },
    resetView() {
      this.fitToScreen();
    },
    onWheel(e) {
      const delta = e.deltaY < 0 ? 0.1 : -0.1;
      this.scale = Math.min(5, Math.max(0.01, this.scale + delta));
    },
    startDrag(e) {
      this.dragging = true;
      this.dragStart = { x: e.clientX, y: e.clientY };
    },
    onDrag(e) {
      if (!this.dragging) return;
      const dx = e.clientX - this.dragStart.x;
      const dy = e.clientY - this.dragStart.y;
      this.translateX += dx;
      this.translateY += dy;
      this.dragStart = { x: e.clientX, y: e.clientY };
    },
    stopDrag() {
      this.dragging = false;
    },
    onTouchStart(e) {
      if (e.touches.length === 1) {
        this.dragging = true;
        this.dragStart = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      } else if (e.touches.length === 2) {
        this.dragging = false;
        this.initialDistance = this.getTouchDistance(e);
        this.initialScale = this.scale;
      }
    },
    onTouchMove(e) {
      if (e.touches.length === 1 && this.dragging) {
        const dx = e.touches[0].clientX - this.dragStart.x;
        const dy = e.touches[0].clientY - this.dragStart.y;
        this.translateX += dx;
        this.translateY += dy;
        this.dragStart = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      } else if (e.touches.length === 2) {
        const dist = this.getTouchDistance(e);
        const scaleFactor = dist / this.initialDistance;
        this.scale = Math.min(
          5,
          Math.max(0.2, this.initialScale * scaleFactor)
        );
      }
    },
    onTouchEnd() {
      this.dragging = false;
    },
    getTouchDistance(e) {
      const [a, b] = e.touches;
      const dx = a.clientX - b.clientX;
      const dy = a.clientY - b.clientY;
      return Math.sqrt(dx * dx + dy * dy);
    },
    // formatDate(dateStr) {
    //   return new Date(dateStr).toLocaleString();
    // },
    // formatSize(bytes) {
    //   const kb = bytes / 1024;
    //   return kb < 1024 ? `${kb.toFixed(1)} KB` : `${(kb / 1024).toFixed(1)} MB`;
    // },
  },
};
</script>

<style lang="less" scoped>
.media-detail {
  position: fixed;
  inset: 0;
  background: rgba(128, 128, 128, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  overflow: hidden;
  touch-action: none;
  color: white;
}

.media-container {
  display: flex;
  flex-direction: row;
  width: 100vw;
  height: 100vh;
  position: relative;

  @media (max-aspect-ratio: 4/3) {
    flex-direction: column;
  }
}

.media-viewer {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;

  img {
    position: absolute;
    user-select: none;
    pointer-events: none;
    will-change: transform;
    width: unset;
    height: unset;
    display: block;
    max-width: none;
    max-height: none;
  }

  video {
    width: 100%;
    height: auto;
    max-height: 100%;
  }
}

.toggle-info-button {
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: rgba(255, 255, 255, 0.5);
  color: white;
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  z-index: 1000;
}
.media-transform {
  position: absolute;
  top: 0;
  left: 0;
  will-change: transform;
}
.face-box-wrapper {
  position: absolute;
  cursor: pointer;
  pointer-events: auto;
  border-color: red;

  .face-label {
    text-rendering: geometricPrecision;
    position: absolute;
    top: -1.3em;
    left: 0;
    background: rgba(255, 255, 255, 0.85);
    color: black;
    padding: 0 0.3em;
    border-radius: 3px;
    white-space: nowrap;
    // overflow: hidden;
    text-overflow: ellipsis;
  }
}
.face-box {
  position: absolute;
  background: #fff;
  opacity: 0.8;

  &.admin-editable {
    pointer-events: auto;
    cursor: pointer;
  }

  // für alle anderen: keine Interaktion
  &:not(.admin-editable) {
    pointer-events: none;
  }
}
.face-box-wrapper.selected {
  border-color: #0c0;
}

.toolbarStyle {
  position: fixed;
  padding: 2vw;
  z-index: 1000;
}

.edit-wrapper {
  position: relative;

  input {
    font-size: inherit;
    padding: 1px 4px;
    position: relative;
  }

  ul.suggestions {
    position: absolute;
    top: 100%;
    left: 0;
    background: white;
    border: 1px solid #ccc;
    z-index: 20;
    max-height: 150px;
    overflow-y: auto;
    list-style: none;
    padding: 0;
    margin: 4px 0 0;

    li {
      padding: 4px 8px;
      cursor: pointer;
      color: black;
      text-align: left;

      &.highlighted {
        background-color: #ddd;
      }
    }
  }
}
</style>